<template>
  <!-- A person: one file on the board. Everything on it is something the
       member told us, something we did, or what the assistant decided — each
       with where it came from. Their own page stays theirs (S15). -->
  <main class="page person-page">
    <p v-if="loading" class="lab">Pulling the file…</p>

    <div v-else-if="error" class="panel sober missing">
      <span class="rstamp returned">Not here</span>
      <h1 class="d3 gap">Can&#8217;t find that person.</h1>
      <p class="sm">{{ error }}</p>
      <router-link to="/my-people" class="btn ghost">Back to your people</router-link>
    </div>

    <template v-else-if="person">
      <div class="panel folder file-head">
        <div class="who">
          <span class="lab">A file on your board</span>
          <h1 class="d2 name">{{ person.name }}</h1>
          <p class="sm facts">{{ facts }}</p>
        </div>
        <span v-if="claimedAt" class="rstamp live claimed">Has a page of their own<span class="since">{{ formatDate(claimedAt) }}</span></span>
      </div>

      <div class="file">
        <div class="main-col">
          <!-- 1 · Where things stand: the assistant's own plan, in its words -->
          <div class="divider"><span class="dtab">Where things stand</span></div>
          <template v-if="plan && plan.intent">
            <p class="lede">{{ plan.intent }}</p>
            <span class="lab sig">— the desk<template v-if="plan.updatedAt"> · {{ formatDate(plan.updatedAt) }}</template></span>
          </template>
          <p v-else class="lede mut">No plan yet. I write one once I have something to go on — the more you tell me, the sooner.</p>

          <dl class="slip">
            <div v-if="holding"><dt>Holding back</dt><dd>{{ plan.hold.why }}<template v-if="plan.hold.until"> · until {{ formatDate(Date.parse(plan.hold.until)) }}</template></dd></div>
            <div v-else-if="plan && plan.waitingOn"><dt>Waiting on</dt><dd>{{ plan.waitingOn }}</dd></div>
            <div v-if="plan && plan.nextTouchAt"><dt>Next look</dt><dd>{{ formatDate(Date.parse(plan.nextTouchAt)) }}<template v-if="plan.channel && plan.channel !== 'none'"> · {{ channelLabel(plan.channel) }}</template></dd></div>
            <div v-if="seen.last"><dt>Last saw them</dt><dd>{{ formatDate(seen.last) }} · {{ ago(seen.last) }} <span class="xs mut">· your calendar</span></dd></div>
            <div v-if="talked"><dt>Last really talked</dt><dd>{{ talked }} <span class="xs mut">· you said, {{ formatDate(person.evidence.lastTalkedAnsweredAt) }}</span></dd></div>
            <div v-if="seen.next"><dt>Seeing them</dt><dd>{{ formatDate(seen.next) }} <span class="xs mut">· your calendar</span></dd></div>
            <div><dt>Last reached</dt><dd>{{ person.lastContactAt ? `${formatDate(person.lastContactAt)} · ${ago(person.lastContactAt)}` : 'Not yet, through us' }}</dd></div>
            <div><dt>Sent so far</dt><dd><span class="num">{{ person.totalSent || 0 }}</span> {{ (person.totalSent || 0) === 1 ? 'thing' : 'things' }}, from you and from me</dd></div>
            <div v-if="nextDate"><dt>Next date</dt><dd>{{ nextDate.label }} · {{ formatMonthDay(nextDate) }} · <span class="num">{{ nextDate.days === 0 ? 'today' : nextDate.days === 1 ? 'tomorrow' : `${nextDate.days} days` }}</span></dd></div>
            <div><dt>On my own</dt><dd>{{ reachLabel }}</dd></div>
          </dl>

          <!-- 2 · What I know, and where I got it -->
          <div class="divider gapped"><span class="dtab">What I know, and where I got it</span><span class="cnt">{{ signals.length }}</span></div>
          <ul v-if="signals.length || vibe.length" class="ev knows">
            <li v-for="s in signals" :key="s.at + s.text">
              <span>{{ s.text }}</span>
              <span class="meta">
                <span>{{ sourceLabel(s) }}<br>{{ formatDate(s.at) }}</span>
                <button type="button" class="strike" :disabled="saving" :aria-label="`Strike out: ${s.text}`" @click="strike(s)">Strike out</button>
              </span>
            </li>
            <li v-if="vibe.length">
              <span>How you described them: {{ vibe.join(', ') }}.</span>
              <span class="meta">When you added them</span>
            </li>
          </ul>
          <p v-else class="sm mut">Nothing yet. Tell me one true thing about {{ firstName }} — a new job, a broken mug, a trip coming up — and it goes into tomorrow&#8217;s reading.</p>
          <p class="xs gap">Nothing here was scraped from the web. Each line is something you told me, or read from your own calendar or contacts because you connected them, and says which. You can strike any of it out.</p>

          <!-- 3 · What has actually reached them -->
          <div class="divider gapped"><span class="dtab">What has actually reached them</span><span class="cnt">{{ history.length }}</span></div>
          <ul v-if="history.length" class="ev">
            <li v-for="h in history" :key="h.key">
              <span>
                <router-link v-if="h.to" :to="h.to">{{ h.text }}</router-link>
                <template v-else>{{ h.text }}</template>
                <span v-if="h.detail" class="detail xs">{{ h.detail }}</span>
              </span>
              <span class="meta"><span class="pill" :class="h.pillCls">{{ h.pill }}</span> {{ formatDate(h.at) }}</span>
            </li>
          </ul>
          <p v-else class="sm mut">Nothing from me yet.<template v-if="person.totalSent"> The {{ person.totalSent }} you sent yourself count above; this list is what I sent or proposed.</template></p>

          <!-- 4 · My reasoning, for the curious -->
          <details v-if="thoughts.length" class="drawer gapped">
            <summary>What I&#8217;ve thought about {{ firstName }} · {{ thoughts.length }}</summary>
            <ul class="ev">
              <li v-for="e in thoughts" :key="e.id">
                <span><span class="lab inl">{{ kindLabel(e.kind) }}</span>{{ e.text }}</span>
                <time>{{ formatDate(e.at) }}</time>
              </li>
            </ul>
          </details>
        </div>

        <aside class="side-col">
          <!-- Their side: that it exists, never what it says -->
          <div class="panel sober">
            <template v-if="claimedAt">
              <span class="lab green">Their side · not yours to read</span>
              <p class="sm gap">
                {{ firstName }} answered. They have a page of their own that says what may reach them, how often, and what
                anyone sending should know. Your assistant reads it before every send and every gift idea, and follows it.
              </p>
              <p class="sm gap">You aren&#8217;t shown what&#8217;s on it, and you aren&#8217;t told when it changes. That&#8217;s the deal, and it&#8217;s why people fill it in.</p>
            </template>
            <template v-else-if="person.recipientRef">
              <span class="lab">Their side · not answered yet</span>
              <p class="sm gap">
                {{ firstName }} has a page waiting, and hasn&#8217;t opened it. Until they do, I go by what you&#8217;ve set here,
                and nothing reaches them that your settings don&#8217;t allow.
              </p>
            </template>
            <template v-else>
              <span class="lab">Their side · none yet</span>
              <p class="sm gap">
                {{ firstName }} doesn&#8217;t have a page of their own. I go by what you&#8217;ve set: {{ reachLabel.toLowerCase() }}.
                A note I send them by email carries a link to make one, if they want it.
              </p>
            </template>
          </div>

          <!-- Tell me something -->
          <div class="panel tell">
            <label class="lab" for="tell">Tell me something</label>
            <p class="xs gap">Anything you&#8217;d write on the back of a photo. I keep it in your words, with the date, and I don&#8217;t tidy it up.</p>
            <textarea id="tell" v-model="tellText" maxlength="300" :placeholder="`${firstName} mentioned the apartment is finally furnished…`"></textarea>
            <div class="chiprow gap">
              <button v-for="c in tellChips" :key="c.label" type="button" class="chip" @click="fill(c.text)"><span class="box" aria-hidden="true"></span>{{ c.label }}</button>
            </div>
            <div class="btnrow gap">
              <button type="button" class="btn" :disabled="!tellText.trim() || saving" @click="file">{{ saving ? 'Filing…' : 'File it' }}</button>
            </div>
            <p class="xs gap">{{ firstName }} never sees what you write here, and it&#8217;s never quoted back to them on a card.</p>
          </div>

          <!-- What happens next -->
          <div class="panel kraftp">
            <span class="lab">What happens next</span>
            <template v-if="pending.length">
              <p v-for="p in pending" :key="p.id" class="sm gap">
                <strong>{{ p.kind === 'gesture' ? 'Something for you to do' : 'Gift ideas are waiting on you' }}</strong><template v-if="p.occasion"> · {{ p.occasion }}</template>.
                <router-link :to="`/gifts/${p.id}`">Look at {{ p.kind === 'gesture' ? 'it' : 'them' }} →</router-link>
              </p>
            </template>
            <p v-else-if="plan && plan.nextTouchAt" class="sm gap">I look at {{ firstName }} again on {{ formatDate(Date.parse(plan.nextTouchAt)) }}. Nothing needs you before then.</p>
            <p v-else class="sm gap">Nothing needs you. I read everyone every morning; if that changes, it&#8217;s on the desk first.</p>
            <div class="btnrow gap">
              <router-link to="/my-people" class="btn ghost">Send something yourself</router-link>
              <router-link to="/my-people" class="btn quiet">Edit their details</router-link>
            </div>
          </div>
        </aside>
      </div>
    </template>
  </main>
</template>

<script>
import { db, auth } from '@/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { monthName } from '@thinking-of-u/shared';
import { isEmailContactable, isPhysicalContactable } from '@/utils/agentEligibility';

const KIND_LABEL = {
  summary: 'Summary', decision: 'Decision', observation: 'Noticed', question: 'Asked you', member_reply: 'You said',
  would_send: 'Would have sent', would_propose: 'Would have proposed', action: 'Sent', proposal: 'Proposed a gift',
  gesture: 'Something for you to do', refusal: 'Their side said no', restraint: 'Holding back', quiet: 'A quiet day, on purpose', note: 'Note',
};
const CHANNEL_LABEL = { email: 'a note by email', physical: 'something in the post', gift: 'a gift', none: 'just watching' };
const ACTION_LABEL = {
  digital_postcard: 'A postcard by email', physical_postcard: 'A postcard in the post',
  handwritten_note: 'A handwritten note', letter: 'A printed letter',
};
const GIFT_STATUS = {
  pending_approval: ['Waiting on you', 'no'], approved: ['Approved', ''], purchasing: ['Ordering', ''],
  purchased: ['Bought', 'ok'], purchase_failed: ['Not ordered', 'no'], declined: ['You passed', ''],
  gesture_chosen: ['Yours to do', 'ok'], pooled: ['Given together', 'ok'],
};
const DAY = 24 * 60 * 60 * 1000;

export default {
  name: 'PersonPage',
  data() {
    return {
      loading: true,
      error: null,
      person: null,
      plan: null,
      actions: [],
      proposals: [],
      journal: [],
      tellText: '',
      saving: false,
      unsubs: [],
    };
  },
  computed: {
    firstName() {
      return ((this.person && this.person.name) || '').split(' ')[0] || 'them';
    },
    claimedAt() {
      return (this.person && this.person.recipientClaimedAt) || null;
    },
    vibe() {
      return (this.person && this.person.vibe) || [];
    },
    // When the member's own calendar says they were (or will be) together.
    seen() {
      const ev = (this.person && this.person.evidence) || {};
      return { last: ev.lastSeenAt || null, next: ev.nextSeenAt && ev.nextSeenAt >= Date.now() ? ev.nextSeenAt : null };
    },
    // The member's answer to the weekly question, in its own (bucketed) words.
    talked() {
      const ev = (this.person && this.person.evidence) || {};
      const label = { week: 'This week', month: 'This month', months: 'A few months ago', longer: 'Longer than you\u2019d like', unsure: 'You couldn\u2019t remember' }[ev.lastTalkedBucket];
      return label && ev.lastTalkedAnsweredAt ? label : '';
    },
    signals() {
      return [...((this.person && this.person.signals) || [])].sort((a, b) => (b.at || 0) - (a.at || 0));
    },
    nextDate() {
      const dates = (this.person && this.person.significantDates) || [];
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let best = null;
      for (const sd of dates) {
        if (!sd.month || !sd.day) continue;
        let t = new Date(today.getFullYear(), sd.month - 1, sd.day);
        if (t < today) t = new Date(today.getFullYear() + 1, sd.month - 1, sd.day);
        const days = Math.round((t - today) / DAY);
        if (!best || days < best.days) best = { ...sd, days };
      }
      return best;
    },
    facts() {
      const p = this.person;
      const bits = [];
      if (p.type) bits.push(p.type.charAt(0).toUpperCase() + p.type.slice(1));
      for (const sd of p.significantDates || []) {
        if (sd.month && sd.day) bits.push(`${(sd.label || 'Date').toLowerCase()} ${sd.day} ${monthName(sd.month)}`);
      }
      return bits.join(' · ');
    },
    // Mirrors the orchestrator's consent gates (as the People cards do), said
    // plainly: what the assistant may do for this person without asking.
    reachLabel() {
      const p = this.person;
      const channel = p.preferredChannel || 'off';
      // A claimed recipient's own page replaces the consent flags (delivery.js).
      if (this.claimedAt && channel === 'email') return 'May write to them by email, inside their own rules';
      if (this.claimedAt && channel === 'physical') return 'May send them things in the post, inside their own rules';
      if (channel === 'email') return isEmailContactable(p) ? 'May write to them by email' : 'Email is set, but it needs their consent or an address';
      if (channel === 'physical') return isPhysicalContactable(p) ? 'May send them things in the post' : 'Post is set, but it needs their consent or an address';
      return 'Off — I only suggest, I never send';
    },
    holding() {
      const h = this.plan && this.plan.hold;
      if (!h || !h.why) return false;
      return !h.until || Date.parse(h.until) > Date.now();
    },
    pending() {
      return this.proposals.filter((p) => p.status === 'pending_approval');
    },
    history() {
      const rows = [];
      for (const a of this.actions) {
        if (a.status === 'generating' || a.status === 'delivering') continue;
        const label = ACTION_LABEL[a.type] || 'Something I sent';
        let pill = 'Delivered'; let pillCls = 'ok'; let detail = '';
        if (a.status === 'sandboxed') { pill = 'Held back'; pillCls = ''; detail = 'Written, then sent to your own inbox instead — nothing reached them.'; }
        if (a.status === 'failed') { pill = 'Didn’t go'; pillCls = 'no'; detail = a.ineligible ? 'Their settings didn’t allow it.' : ''; }
        rows.push({ key: `a-${a.id}`, at: a.createdAt, text: label, detail, pill, pillCls });
      }
      for (const p of this.proposals) {
        const [pill, pillCls] = GIFT_STATUS[p.status] || ['Proposed', ''];
        const pickedId = p.decision && p.decision.choice === 'approve' && p.decision.candidateId;
        const chosen = pickedId ? (p.proposals || []).find((c) => c.candidateId === pickedId) : null;
        const n = (p.proposals || []).length;
        const text = p.kind === 'gesture' ?
          `Something to do — ${(p.proposals && p.proposals[0] && p.proposals[0].title) || 'a gesture'}` :
          chosen ? `A gift — ${chosen.title}` : `${n} gift idea${n === 1 ? '' : 's'}${p.occasion ? ` · ${p.occasion}` : ''}`;
        const detail = p.decision && p.decision.choice === 'decline' && p.decision.reason ? `You said “${p.decision.reason}”.` : '';
        rows.push({ key: `g-${p.id}`, at: p.createdAt, text, detail, pill, pillCls, to: `/gifts/${p.id}` });
      }
      return rows.sort((x, y) => (y.at || 0) - (x.at || 0));
    },
    thoughts() {
      return this.journal
          .filter((e) => e.text && !['run_end', 'memory', 'error'].includes(e.kind))
          .sort((a, b) => (b.at || 0) - (a.at || 0))
          .slice(0, 40);
    },
    tellChips() {
      const n = this.firstName;
      return [
        { label: 'Something’s off', text: `${n}'s been low lately.` },
        { label: 'A date is coming', text: `${n}'s got something coming up: ` },
        { label: 'Back off a bit', text: `Don't send ${n} anything for a few weeks.` },
      ];
    },
  },
  created() {
    this.stopAuth = onAuthStateChanged(auth, (user) => {
      this.unsubs.forEach((u) => u());
      this.unsubs = [];
      if (user) this.subscribe(user.uid);
    });
  },
  beforeUnmount() {
    this.unsubs.forEach((u) => u());
    if (this.stopAuth) this.stopAuth();
  },
  watch: {
    '$route.params.id'() {
      if (auth.currentUser) {
        this.unsubs.forEach((u) => u());
        this.unsubs = [];
        this.subscribe(auth.currentUser.uid);
      }
    },
  },
  methods: {
    sourceLabel(s) {
      if (s.source === 'calendar') return 'Your calendar';
      if (s.source === 'contacts') return 'Your contacts';
      if (s.source === 'imessage') return 'Your messages';
      return 'You told me';
    },
    subscribe(uid) {
      const id = this.$route.params.id;
      this.loading = true;
      this.error = null;
      const soft = (what) => (e) => console.warn(`PersonPage: could not read ${what}:`, e && e.message);
      this.unsubs.push(onSnapshot(doc(db, 'relationships', id), (snap) => {
        this.loading = false;
        if (!snap.exists() || snap.data().userId !== uid) {
          this.person = null;
          this.error = 'They aren’t on your board — or the link is out of date.';
          return;
        }
        this.person = { id: snap.id, ...snap.data() };
      }, () => {
        this.loading = false;
        this.error = 'They aren’t on your board — or the link is out of date.';
      }));
      // Equality-only queries: served without a composite index.
      this.unsubs.push(onSnapshot(query(collection(db, 'agentPlans'), where('userId', '==', uid), where('relationshipId', '==', id)), (snap) => {
        this.plan = snap.docs.length ? snap.docs[0].data() : null;
      }, soft('the plan')));
      this.unsubs.push(onSnapshot(query(collection(db, 'agentActions'), where('userId', '==', uid), where('relationshipId', '==', id)), (snap) => {
        this.actions = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }, soft('what was sent')));
      this.unsubs.push(onSnapshot(query(collection(db, 'giftProposals'), where('userId', '==', uid), where('relationshipId', '==', id)), (snap) => {
        this.proposals = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }, soft('gift ideas')));
      this.unsubs.push(onSnapshot(query(collection(db, 'agentJournal', uid, 'entries'), where('relationshipId', '==', id)), (snap) => {
        this.journal = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }, soft('the journal')));
    },
    kindLabel(kind) {
      return KIND_LABEL[kind] || 'Note';
    },
    channelLabel(channel) {
      return CHANNEL_LABEL[channel] || channel || '';
    },
    formatDate(ms) {
      if (!ms) return '';
      return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    formatMonthDay(sd) {
      return `${sd.day} ${monthName(sd.month)}`;
    },
    ago(ms) {
      const days = Math.floor((Date.now() - ms) / DAY);
      if (days < 1) return 'today';
      if (days === 1) return 'yesterday';
      if (days < 45) return `${days} days ago`;
      const months = Math.round(days / 30);
      return `${months} month${months === 1 ? '' : 's'} ago`;
    },
    fill(text) {
      const t = this.tellText.replace(/\s+$/, '');
      this.tellText = t ? `${t} ${text}` : text;
      this.$nextTick(() => { const el = document.getElementById('tell'); if (el) el.focus(); });
    },
    // Signals are the member's own words, newest last, capped at 20 (the brief
    // reads the recent tail) — the same store the People cards write.
    async writeSignals(next, extra = {}) {
      this.saving = true;
      try {
        await updateDoc(doc(db, 'relationships', this.person.id), { signals: next, ...extra, updatedAt: Date.now() });
        return true;
      } catch (e) {
        this.$toast.error('Couldn’t save that — try again.');
        return false;
      } finally {
        this.saving = false;
      }
    },
    async file() {
      const text = this.tellText.trim();
      if (!text) return;
      const at = Date.now();
      const ok = await this.writeSignals([...(this.person.signals || []).slice(-19), { text, at, source: 'member', kind: 'note', ref: `jot:page:${at}` }]);
      if (ok) {
        this.tellText = '';
        this.$toast.success('Filed. It goes into tomorrow’s reading.');
      }
    },
    async strike(s) {
      const next = (this.person.signals || []).filter((x) => !(x.at === s.at && x.text === s.text));
      // A struck calendar line stops counting as a time you saw them, too.
      // Its ref stays remembered, so tomorrow's read doesn't bring it back.
      const ev = this.person.evidence;
      let extra = {};
      if (ev && s.source === 'calendar' && (s.kind === 'met' || s.kind === 'upcoming')) {
        const metAt = (ev.metAt || []).filter((t) => !(s.kind === 'met' && t === s.at));
        const past = metAt.filter((t) => t <= Date.now());
        extra = { evidence: {
          ...ev,
          metAt,
          lastSeenAt: past.length ? Math.max(...past) : null,
          seenCount90d: past.filter((t) => t >= Date.now() - 90 * DAY).length,
          nextSeenAt: s.kind === 'upcoming' && ev.nextSeenAt === s.at ? null : (ev.nextSeenAt || null),
        } };
      }
      if (await this.writeSignals(next, extra)) this.$toast.success('Struck out. I won’t go on it again.');
    },
  },
};
</script>

<style scoped>
.person-page { width: 100%; box-sizing: border-box; }
.gap { margin-top: 10px; }
.missing { max-width: 34rem; }
.missing .btn { margin-top: 16px; }

.file-head { display: flex; flex-wrap: wrap; gap: 14px 20px; align-items: flex-end; padding-bottom: 26px; }
.who { flex: 1 1 260px; }
.name { margin-top: 8px; }
.facts { margin-top: 6px; color: var(--ink-3); }
.claimed { flex: none; text-align: center; }
.since { display: block; font-size: .82em; letter-spacing: .14em; margin-top: 3px; }

.file { display: grid; gap: clamp(28px, 3.6vw, 48px); margin-top: 34px; }
@media (min-width: 1000px) { .file { grid-template-columns: minmax(0, 1.5fr) minmax(280px, .8fr); align-items: start; } }
.side-col { display: grid; gap: 20px; }
@media (min-width: 1000px) { .side-col { position: sticky; top: 20px; } }
.divider.gapped, .drawer.gapped { margin-top: 44px; }

.main-col .lede { margin-top: 16px; max-width: 62ch; white-space: pre-line; }
.sig { display: block; margin-top: 8px; letter-spacing: .14em; }
.mut { color: var(--ink-3); }

.slip { margin: 22px 0 0; display: grid; grid-template-columns: auto 1fr; border-top: 1px solid var(--rule); max-width: 40rem; }
.slip > div { display: contents; }
.slip dt {
  font-family: var(--mono); font-size: .62rem; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-3);
  padding: 9px 16px 8px 0; border-bottom: 1px solid var(--rule); white-space: nowrap;
}
.slip dd { margin: 0; padding: 8px 0; border-bottom: 1px solid var(--rule); font-size: .88rem; color: var(--ink-2); }

.ev { margin-top: 14px; }
.ev .meta { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; text-align: right; white-space: nowrap;
  font-family: var(--mono); font-size: .62rem; letter-spacing: .1em; text-transform: uppercase; color: var(--ink-3); }
.knows .meta { line-height: 1.45; }
.strike {
  appearance: none; background: none; border: 0; padding: 0; margin: 0; cursor: pointer; color: var(--ink-3);
  font: inherit; text-decoration: underline; text-underline-offset: 3px;
}
.strike:hover { color: var(--red-ink); }
.detail { display: block; margin-top: 3px; color: var(--ink-3); }
.inl { margin-right: 8px; }

.tell textarea { margin-top: 12px; min-height: 92px; }
</style>
